import { AxiosError } from 'axios';

import {
  axiosInstance,
  axiosInstanceForPublicAPI,
  axiosInstanceForPublicAPIWithAuth,
  axiosInstanceForTenantAPI,
  axiosInstanceWithSlugForPublicAPI,
} from '../assets/utils/axiosInstance';
import { CustomerListItemPatch } from '../components/accessRolesModalComponents/customerAccessComponent';
import {
  AdminPiiRemovalRequestBody,
  CUSTOMER_AUTH_LOGIN,
  TenantPiiRemovalRequestBody,
} from '../constants/customerConstant';
import { getAuthenticatedUserTenantDetails } from '../services/customerConfigService';

interface ErrorResponse {
  message: string;
}

const customerAuthService = {
  // Customer Login
  customerLogin: async (username: string, password: string) => {
    // Customer Username and Password Combination Login
    try {
      const response = await axiosInstance().post('/tenant/login', {
        username: username,
        password: password,
        is_http_cookie_only: true,
      });

      if (
        Object.prototype.hasOwnProperty.call(response.data, 'ephemeral_token')
      ) {
        return response.data;
      }

      // HAVE THIS UTILIZE THE COOKIES IN ITS CALL LET IT USE AXIOS INSTANCE
      const { slug } = await getAuthenticatedUserTenantDetails();

      return { slug: slug, data: response.data };
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }

    // Error Handling for require username&password or key
    // throw new Error('Username/Email and Password or key required');
  },

  // Customer Register
  customerRegister: async (email: string, password: string) => {
    try {
      const response = await axiosInstance().post('/tenant/register', {
        username: email,
        password: password,
        is_http_cookie_only: true,
      });

      const { slug } = await getAuthenticatedUserTenantDetails();

      return { slug, tokens: response.data };
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Login OTP
  loginOtp: async (ephemeralToken: string, otpCode: string) => {
    // Customer Ephemeral Code and OTP code recieve in users email
    try {
      const response = await axiosInstance().post(
        CUSTOMER_AUTH_LOGIN.USER_OTP,
        {
          ephemeral_token: ephemeralToken,
          code: otpCode,
          is_http_cookie_only: true,
        },
      );

      const { slug } = await getAuthenticatedUserTenantDetails();

      return { slug, tokens: response.data };
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
    // throw new Error('OTP is required');
  },

  // Login with Google oAuth
  googleLogin: async (googleCode: string) => {
    try {
      const response = await axiosInstance().post('/tenant/login/google', {
        code: googleCode,
        is_http_cookie_only: true,
      });

      const { slug } = await getAuthenticatedUserTenantDetails();

      return { slug, tokens: response.data };
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Resend OTP Code
  resendOtpCode: async (ephemeralToken: string | null) => {
    try {
      const response = await axiosInstanceForPublicAPI().post(
        '/tenant/login/resend-code',
        {
          ephemeral_token: ephemeralToken,
          for_resending: true,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Update A user type 1 and 2 password that are NOT google authenticated users
  updateUserPassword: async (updatedPassword: string, slug: string | null) => {
    try {
      const response = await axiosInstanceForTenantAPI(slug).post(
        '/reset-password',
        {
          password: updatedPassword,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Send a reset password email to an existing users email address
  sendResetEmail: async (userEmail: string) => {
    try {
      const response = await axiosInstanceForPublicAPIWithAuth().post(
        '/send-reset-password-email',
        {
          email: userEmail,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  sendForgetPasswordResetEmailCustomer: async (userEmail: string) => {
    try {
      const response = await axiosInstanceForPublicAPI().post(
        '/send-reset-password-email',
        {
          email: userEmail,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  sendForgetPasswordResetEmailWhistleblower: async (userEmail: string) => {
    try {
      const response = await axiosInstanceWithSlugForPublicAPI().post(
        '/send-reset-password-email',
        {
          email: userEmail,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Reset password for a user
  customerResetPassword: async (
    newPassword: string,
    userUid: string,
    resetToken: string,
  ) => {
    try {
      const response = await axiosInstanceForPublicAPI().post(
        `/tenant/${userUid}/${resetToken}/reset-password`,
        {
          password: newPassword,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  //Get User Roles for Case Manager
  getAccessRoles: async () => {
    try {
      const response = await axiosInstance().get('/user/flags');
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Get User Role Settings for Access Role in Customer Settings
  getUserRoleSettings: async (userId: number, slug: string | null) => {
    try {
      const response = await axiosInstanceForTenantAPI(slug).get(
        `/users/${userId}/role-settings`,
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Update flag to only View Assigned Reports
  updateOnlyViewAssignedReportsFlag: async (
    userId: number,
    slug: string | null,
    viewReportFlag: boolean | null,
  ) => {
    try {
      const response = await axiosInstanceForTenantAPI(slug).patch(
        `/users/${userId}/role-settings`,
        {
          is_only_allowed_to_view_assigned_report: viewReportFlag,
          is_http_cookie_only: true,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Update Flag to only View Assigned Reports based on Users department
  updateOnlyViewDepartmentAssignedReportsFlag: async (
    userId: number,
    slug: string | null,
    viewReportFlag: boolean | null,
  ) => {
    try {
      const response = await axiosInstanceForTenantAPI(slug).patch(
        `/users/${userId}/role-settings`,
        {
          is_only_allowed_to_view_report_in_department: viewReportFlag,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Update Allow Reopen Report flag
  updateAllowReopenReportFlag: async (
    userId: number,
    slug: string | null,
    allowReopenReportFlag: boolean | null,
  ) => {
    try {
      const response = await axiosInstanceForTenantAPI(slug).patch(
        `/users/${userId}/role-settings`,
        {
          is_allowed_to_reopen_report: allowReopenReportFlag,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Update Users Allowed Report Type ID's
  updateAllowReportTypes: async (
    userId: number,
    slug: string | null,
    allowedReportTypes: number[],
  ) => {
    try {
      const response = await axiosInstanceForTenantAPI(slug).patch(
        `/users/${userId}/role-settings`,
        {
          allowed_report_type_ids: allowedReportTypes,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Update Users Allowed/Restricted Access to specific Report ID's
  updateAllowedSpecificReportAccess: async (
    userId: number,
    slug: string | null,
    ReportIds: string[],
    type: 'allow' | 'restrict',
  ) => {
    const payload =
      type === 'allow'
        ? {
            allowed_report_access: ReportIds,
          }
        : {
            restrict_report_access: ReportIds,
          };
    try {
      const response = await axiosInstanceForTenantAPI(slug).patch(
        `/users/${userId}/role-settings`,
        payload,
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  updateCustomerAccess: async (
    userId: number,
    slug: string | null,
    allowedCustomerAccess: CustomerListItemPatch[],
  ) => {
    try {
      const response = await axiosInstanceForTenantAPI(slug).patch(
        `/users/${userId}/role-settings`,
        {
          allowed_tenants: allowedCustomerAccess,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Update Allow Removal of PII
  updateAllowRemovalOfPIIFlag: async (
    userId: number,
    slug: string | null,
    allowAllowRemovalOfPIIFlag: boolean | null,
  ) => {
    try {
      const response = await axiosInstanceForTenantAPI(slug).patch(
        `/users/${userId}/role-settings`,
        {
          is_allowed_to_request_pii_removal: allowAllowRemovalOfPIIFlag,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Update Allow Redacting Report
  updateAllowRedactingReportFlag: async (
    userId: number,
    slug: string | null,
    allowRedactingOfReportFlag: boolean | null,
  ) => {
    try {
      const response = await axiosInstanceForTenantAPI(slug).patch(
        `/users/${userId}/role-settings`,
        {
          is_allowed_to_redact_report: allowRedactingOfReportFlag,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Update Only View Report Flag
  updateOnlyViewReportFlag: async (
    userId: number,
    slug: string | null,
    OnlyViewReportFlag: boolean | null,
  ) => {
    try {
      const response = await axiosInstanceForTenantAPI(slug).patch(
        `/users/${userId}/role-settings`,
        {
          is_read_only_report: OnlyViewReportFlag,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Update Allowed to edit report
  updateAllowedToEditReport: async (
    userId: number,
    slug: string | null,
    fieldName: string | null,
    fieldValue: boolean | null,
  ) => {
    try {
      const response = await axiosInstanceForTenantAPI(slug).patch(
        `/users/${userId}/role-settings`,
        {
          [String(fieldName)]: fieldValue,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Update Allowed to craete final report template
  updateAllowCreateFinalReportTemplate: async (
    userId: number,
    slug: string | null,
    AllowCreateFinalReportTemplateFlag: boolean | null,
  ) => {
    try {
      const response = await axiosInstanceForTenantAPI(slug).patch(
        `/users/${userId}/role-settings`,
        {
          is_allowed_to_create_final_report_template:
            AllowCreateFinalReportTemplateFlag,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Update Allowed to Download the Final Report
  updateAllowDownloadFinalReport: async (
    userId: number,
    slug: string | null,
    AllowDownloadFinalReportFlag: boolean | null,
  ) => {
    try {
      const response = await axiosInstanceForTenantAPI(slug).patch(
        `/users/${userId}/role-settings`,
        {
          is_allowed_to_download_final_report: AllowDownloadFinalReportFlag,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Update Allowed to Download Attachments
  updateAllowDownloadAttachments: async (
    userId: number,
    slug: string | null,
    AllowDownloadAttachments: boolean | null,
  ) => {
    try {
      const response = await axiosInstanceForTenantAPI(slug).patch(
        `/users/${userId}/role-settings`,
        {
          is_allowed_to_download_attachments: AllowDownloadAttachments,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  // Update redacted Description ranges
  updateRedactedDescriptionRanges: async (
    reportId: string,
    slug: string,
    redactedDescriptionRanges: [number, number][] | null,
    descriptionRedactedFlag: boolean,
  ) => {
    try {
      const response = await axiosInstanceForTenantAPI(slug).patch(
        `/report/update/${reportId}`,
        {
          description_redacted_ranges: redactedDescriptionRanges,
          is_description_redacted: descriptionRedactedFlag,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },

  //Tenant Request PII Removal
  tenantPiiRemovalFlags: async (
    reportId: string,
    removeReporterName?: boolean,
    removeCountry?: boolean,
    removeAllAttachmentsFlag?: boolean,
    piiRemovedDescriptionRanges?: [number, number][] | null,
    piiRemovedMessagesRanges?:
      | { id: string; content_pii_removed_ranges: [number, number][] }[]
      | null,
  ) => {
    try {
      const requestBody: TenantPiiRemovalRequestBody = {};

      if (removeReporterName) {
        requestBody.remove_full_name = true;
      } else {
        requestBody.remove_full_name = false;
      }
      if (removeCountry) {
        requestBody.remove_country = true;
      } else {
        requestBody.remove_country = false;
      }
      if (removeAllAttachmentsFlag) {
        requestBody.remove_files = true;
      } else {
        requestBody.remove_files = false;
      }
      if (piiRemovedDescriptionRanges != null) {
        requestBody.description_pii_removed_ranges =
          piiRemovedDescriptionRanges;
      }
      if (piiRemovedMessagesRanges != null) {
        requestBody.messages = piiRemovedMessagesRanges;
      }
      requestBody.report_reference = reportId;
      const response = await axiosInstanceForPublicAPIWithAuth().post(
        '/report-pii-removal-request',
        {
          ...requestBody,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },
  // Admin Delete content of PII
  adminPiiRemovalFlags: async (
    slug: string,
    reportId: string,
    removeReporterName?: boolean,
    removeCountry?: boolean,
    removeAllAttachmentsFlag?: boolean,
    piiRemovedDescriptionRanges?: [number, number][] | null,
    piiRemovedMessagesRanges?:
      | { id: string; content_pii_removed_ranges: [number, number][] }[]
      | null,
  ) => {
    try {
      const requestBody: AdminPiiRemovalRequestBody = {};

      if (removeReporterName) {
        requestBody.full_name = null;
      }
      if (removeCountry) {
        requestBody.country = null;
      }
      if (removeAllAttachmentsFlag) {
        requestBody.files = null;
      }
      if (piiRemovedDescriptionRanges != null) {
        requestBody.description_pii_removed_ranges =
          piiRemovedDescriptionRanges;
      }
      if (piiRemovedMessagesRanges != null) {
        requestBody.pii_removed_messages = piiRemovedMessagesRanges;
      }
      const response = await axiosInstanceForTenantAPI(slug).patch(
        `/report/update/${reportId}`,
        {
          ...requestBody,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },
  approvePiiRemovalRequest: async (requestId: number) => {
    try {
      const response = await axiosInstanceForPublicAPIWithAuth().post(
        `/report-pii-removal-request/${requestId}/approve`,
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },
  denyPiiRemovalRequest: async (requestId: number) => {
    try {
      const response = await axiosInstanceForPublicAPIWithAuth().post(
        `/report-pii-removal-request/${requestId}/reject`,
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },
};

export default customerAuthService;
